import React, { useEffect, useState } from 'react';

import {
  BrazeContentCard,
  HomeAlertCard,
  extractDataAutomationId,
  filterMetadata,
  validateDependencies,
} from 'root/widgets';
import type { HomeAlertCardProps } from 'root/widgets';

import { useComponentInView } from 'root/widgets/common-components/hooks';

import { EVENT_NAMES, emit } from 'root/widgets/events';
import { getContentCards, logCardClick } from 'root/widgets/utils';

import './new-home-alert-card-group.scss';
import type { NewHomeAlertCardGroupProps, RawNewHomeAlertData } from './types';
import { createCardData, filterNewHomeAlertCards, getNewHomeAlertDataFromStorage } from './utils';

const newHomeAlertCardGroupDependencies = ['searchService', 'logError', 'brazeSdk'];

const NewHomeAlertCardGroup: React.FC<NewHomeAlertCardGroupProps> = (props) => {
  const { label, cardConfig, districtConfig, brazeAPIKey, context, metadata, dependencies, additionalEvents } = props;
  validateDependencies(dependencies, newHomeAlertCardGroupDependencies);

  const { brazeSdk } = dependencies;

  const [rawHomeAlerts, setRawHomeAlerts] = useState<Array<RawNewHomeAlertData>>([]);
  const [homeAlerts, setHomeAlerts] = useState<Array<HomeAlertCardProps>>([]);

  const cardGroupRef = useComponentInView(additionalEvents?.IN_VIEWPORT, { ...context, homeAlerts: rawHomeAlerts });

  const handleCardClick = (event, alert, index) => {
    event.preventDefault();

    emit(EVENT_NAMES.NEW_HOME_ALERT_CARD.CLICK, { ...context, data: { homeAlert: alert, index } });

    if (brazeSdk) {
      const rawHomeAlertCard = filterNewHomeAlertCards(
        (getContentCards(brazeSdk)?.cards || []) as Array<BrazeContentCard> | [],
      )[0];

      if (rawHomeAlertCard) {
        logCardClick(brazeSdk, rawHomeAlertCard);
      }
    }

    window.location.href = alert.redirectUrl;
  };

  useEffect(() => {
    const processHomeAlertCards = async () => {
      const rawHomeAlertsData = await getNewHomeAlertDataFromStorage(brazeAPIKey, dependencies);

      if (!rawHomeAlertsData?.length) {
        return;
      }

      setRawHomeAlerts(rawHomeAlertsData);

      const recommendedAlerts = createCardData(rawHomeAlertsData, cardConfig, districtConfig, handleCardClick);
      setHomeAlerts(recommendedAlerts);
    };

    if (brazeAPIKey && brazeSdk) {
      void processHomeAlertCards();
    }
  }, [brazeAPIKey, brazeSdk]);

  return (
    <div
      className="new-home-alert-card-group-root"
      {...filterMetadata(metadata)}
      {...extractDataAutomationId(metadata)}
    >
      {homeAlerts.length > 0 && (
        <>
          <div className="card-group-title">{label}</div>
          <div ref={cardGroupRef} className="card-group-body">
            {homeAlerts.map((alert, index) => (
              <HomeAlertCard key={alert.id || `alert-card-${index}`} {...alert} context={context} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

NewHomeAlertCardGroup.displayName = 'NewHomeAlertCardGroup';

export default NewHomeAlertCardGroup;
